import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import "./OpsMobileReleases.css";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const initialForm = {
  versionName: "",
  versionCode: "",
  changelog: "",
  isForceUpdate: false,
  downloadUrl: "",
};

const humanFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const val = Number(bytes);
  if (Number.isNaN(val)) return "—";
  const unit = Math.min(units.length - 1, Math.floor(Math.log(val) / Math.log(1024)));
  const size = val / Math.pow(1024, unit);
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

export default function OpsMobileReleases() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [apkFile, setApkFile] = useState(null);
  const [fileKey, setFileKey] = useState(Date.now());
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [editingRelease, setEditingRelease] = useState(null);
  const [editForm, setEditForm] = useState(initialForm);
  const [savingEdit, setSavingEdit] = useState(false);
  const [senderFilters, setSenderFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filterForm, setFilterForm] = useState({ senderPattern: "", companyId: "", note: "" });
  const [creatingFilter, setCreatingFilter] = useState(false);
  const { confirmProps, openConfirm } = useConfirmDialog();

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("userToken")}`,
    }),
    []
  );

  const loadReleases = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/mobile/releases`, { headers });
      setReleases(data);
      setError("");
    } catch (err) {
      console.error("Failed to load releases", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to load releases right now.");
    } finally {
      setLoading(false);
    }
  };

  const loadSenderFilters = async () => {
    try {
      setFiltersLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/sender-filters`, { headers });
      setSenderFilters(data);
    } catch (err) {
      console.error("Failed to load sender filters", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to load sender filters.");
    } finally {
      setFiltersLoading(false);
    }
  };

  useEffect(() => {
    loadReleases();
    loadSenderFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2500);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!apkFile) {
      setError("Please choose an APK file.");
      return;
    }
    if (!form.versionName || !form.versionCode) {
      setError("Version name and code are required.");
      return;
    }
    try {
      setUploading(true);
      setError("");
      const payload = new FormData();
      payload.append("apk", apkFile);
      payload.append("versionName", form.versionName);
      payload.append("versionCode", form.versionCode);
      payload.append("changelog", form.changelog);
      payload.append("isForceUpdate", form.isForceUpdate);

      await axios.post(`${API_BASE_URL}/ops/mobile/releases/upload`, payload, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      setForm(initialForm);
      setApkFile(null);
      setFileKey(Date.now());
      showToast("Release uploaded");
      loadReleases();
    } catch (err) {
      console.error("Upload failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to upload APK.");
    } finally {
      setUploading(false);
    }
  };

  const toggleForceUpdate = async (release) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/ops/mobile/releases/${release.id}`,
        { isForceUpdate: !release.is_force_update },
        { headers }
      );
      loadReleases();
      showToast("Release updated");
    } catch (err) {
      console.error("Toggle force update failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to update release.");
    }
  };

  const beginEdit = (release) => {
    setEditingRelease(release);
    setEditForm({
      versionName: release.version_name || "",
      versionCode: release.version_code || "",
      changelog: release.changelog || "",
      downloadUrl: release.download_url || "",
      isForceUpdate: release.is_force_update,
    });
    setError("");
  };

  const cancelEdit = () => {
    setEditingRelease(null);
    setEditForm(initialForm);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingRelease) return;
    try {
      setSavingEdit(true);
      const payload = {
        versionName: editForm.versionName,
        versionCode: editForm.versionCode,
        changelog: editForm.changelog,
        downloadUrl: editForm.downloadUrl,
        isForceUpdate: editForm.isForceUpdate,
      };
      await axios.patch(`${API_BASE_URL}/ops/mobile/releases/${editingRelease.id}`, payload, {
        headers,
      });
      showToast("Release updated");
      cancelEdit();
      loadReleases();
    } catch (err) {
      console.error("Edit failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to update release.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteRelease = async (release) => {
    try {
      await axios.delete(`${API_BASE_URL}/ops/mobile/releases/${release.id}`, { headers });
      showToast("Release deleted");
      if (editingRelease?.id === release.id) {
        cancelEdit();
      }
      loadReleases();
    } catch (err) {
      console.error("Delete failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to delete release.");
    }
  };

  const submitFilter = async (e) => {
    e.preventDefault();
    if (!filterForm.senderPattern.trim()) {
      setError("Sender pattern is required.");
      return;
    }
    try {
      setCreatingFilter(true);
      setError("");
      const payload = {
        senderPattern: filterForm.senderPattern,
        companyId: filterForm.companyId || null,
        note: filterForm.note || null,
      };
      await axios.post(`${API_BASE_URL}/ops/sender-filters`, payload, { headers });
      setFilterForm({ senderPattern: "", companyId: "", note: "" });
      showToast("Sender filter added");
      loadSenderFilters();
    } catch (err) {
      console.error("Create filter failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to create sender filter.");
    } finally {
      setCreatingFilter(false);
    }
  };

  const deleteFilter = async (filterId) => {
    try {
      await axios.delete(`${API_BASE_URL}/ops/sender-filters/${filterId}`, { headers });
      showToast("Sender filter deleted");
      loadSenderFilters();
    } catch (err) {
      console.error("Delete sender filter failed", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to delete sender filter.");
    }
  };

  const confirmDeleteRelease = (release) => {
    openConfirm({
      title: "Delete Release",
      message: `Delete release v${release.version_name}?`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => deleteRelease(release),
    });
  };

  const confirmDeleteFilter = (filter) => {
    openConfirm({
      title: "Delete Sender Filter",
      message: "Delete this sender filter? This action cannot be undone.",
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => deleteFilter(filter.id),
    });
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast("Download link copied");
    } catch (err) {
      console.error("Clipboard failed", err);
      setError("Unable to copy link.");
    }
  };

  return (
    <>
      <div className="ops-mobile-page">
      <div className="ops-mobile-grid">
        <section className="ops-card upload-card">
          <header>
            <div>
              <p className="eyebrow">New Release</p>
              <h3>Upload Pairfon APK</h3>
            </div>
          </header>
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          {toast && <div className="alert alert-success mb-3">{toast}</div>}
          <form onSubmit={handleUpload} className="upload-form">
            <label className="form-label">
              Version Name
              <input
                type="text"
                name="versionName"
                value={form.versionName}
                onChange={handleInputChange}
                className="form-control"
                placeholder="e.g. 1.5.0"
                required
              />
            </label>
            <label className="form-label">
              Version Code
              <input
                type="number"
                name="versionCode"
                value={form.versionCode}
                onChange={handleInputChange}
                className="form-control"
                placeholder="105"
                required
                min="1"
              />
            </label>
            <label className="form-label">
              Changelog
              <textarea
                name="changelog"
                value={form.changelog}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="Short description of changes"
              />
            </label>
            <label className="form-label">
              APK File
              <input
                key={fileKey}
                type="file"
                accept=".apk"
                onChange={(e) => setApkFile(e.target.files?.[0] || null)}
                className="form-control"
              />
            </label>
            <label className="form-check">
              <input
                type="checkbox"
                name="isForceUpdate"
                checked={form.isForceUpdate}
                onChange={handleInputChange}
              />
              <span>Force update (users must install before continuing)</span>
            </label>
            <button className="btn btn-primary mt-2" type="submit" disabled={uploading}>
              {uploading ? "Uploading..." : "Upload APK"}
            </button>
          </form>
        </section>

        <section className="ops-card releases-card">
          <header>
            <div>
              <p className="eyebrow">Release History</p>
              <h3>Published Builds</h3>
            </div>
            <button className="btn btn-outline-secondary" onClick={loadReleases} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </header>
          {loading ? (
            <p className="text-muted mb-0">Loading releases…</p>
          ) : releases.length === 0 ? (
            <p className="text-muted mb-0">No releases recorded yet.</p>
          ) : (
            <div className="table-responsive ops-release-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Metadata</th>
                    <th>Download</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {releases.map((release) => (
                    <tr key={release.id}>
                      <td>
                        <strong>v{release.version_name}</strong>
                        <div className="text-muted small">Code {release.version_code}</div>
                        <div className="text-muted small">{formatDate(release.created_at)}</div>
                      </td>
                      <td>
                        {release.changelog ? (
                          <p className="mb-1">{release.changelog}</p>
                        ) : (
                          <p className="text-muted mb-1">No changelog.</p>
                        )}
                        <div className="text-muted small">
                          Size: {humanFileSize(release.file_size)} &nbsp;•&nbsp; SHA256:{" "}
                          {release.checksum?.slice(0, 12) || "—"}
                        </div>
                        <div className="text-muted small">
                          Force update: {release.is_force_update ? "Yes" : "No"}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => copyLink(release.download_url)}
                        >
                          Copy link
                        </button>
                      </td>
                      <td>
                        <div className="release-actions">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleForceUpdate(release)}
                          >
                            {release.is_force_update ? "Mark Optional" : "Force Update"}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => beginEdit(release)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => confirmDeleteRelease(release)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {editingRelease && (
        <section className="ops-card edit-card">
          <header>
            <div>
              <p className="eyebrow">Edit Release</p>
              <h3>v{editingRelease.version_name}</h3>
            </div>
            <button className="btn btn-outline-secondary" onClick={cancelEdit}>
              Cancel
            </button>
          </header>
          <form onSubmit={submitEdit} className="upload-form">
            <label className="form-label">
              Version Name
              <input
                type="text"
                name="versionName"
                value={editForm.versionName}
                onChange={handleEditChange}
                className="form-control"
                required
              />
            </label>
            <label className="form-label">
              Version Code
              <input
                type="number"
                name="versionCode"
                value={editForm.versionCode}
                onChange={handleEditChange}
                className="form-control"
                required
                min="1"
              />
            </label>
            <label className="form-label">
              Download URL
              <input
                type="text"
                name="downloadUrl"
                value={editForm.downloadUrl}
                onChange={handleEditChange}
                className="form-control"
                required
              />
            </label>
            <label className="form-label">
              Changelog
              <textarea
                name="changelog"
                value={editForm.changelog}
                onChange={handleEditChange}
                className="form-control"
                rows="3"
              />
            </label>
            <label className="form-check">
              <input
                type="checkbox"
                name="isForceUpdate"
                checked={editForm.isForceUpdate}
                onChange={handleEditChange}
              />
              <span>Force update</span>
            </label>
            <button className="btn btn-primary" type="submit" disabled={savingEdit}>
              {savingEdit ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </section>
      )}

      <section className="ops-card filters-card">
        <header>
          <div>
            <p className="eyebrow">Inbound Filtering</p>
            <h3>Blocked Senders</h3>
            <p className="text-muted mb-0">
              Messages from these senders never leave the device. Leave Company ID blank to block globally.
            </p>
          </div>
        </header>

        <form className="filters-form" onSubmit={submitFilter}>
          <label className="form-label">
            Sender (exact match)
            <input
              type="text"
              name="senderPattern"
              value={filterForm.senderPattern}
              onChange={handleFilterChange}
              className="form-control"
              placeholder="e.g. VK-PAIRFN"
              required
            />
          </label>
          <label className="form-label">
            Company ID (optional)
            <input
              type="text"
              name="companyId"
              value={filterForm.companyId}
              onChange={handleFilterChange}
              className="form-control"
              placeholder="Applies only to this tenant"
            />
          </label>
          <label className="form-label">
            Note
            <input
              type="text"
              name="note"
              value={filterForm.note}
              onChange={handleFilterChange}
              className="form-control"
              placeholder="Short description"
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={creatingFilter}>
            {creatingFilter ? "Saving..." : "Add Sender"}
          </button>
        </form>

        <div className="table-responsive ops-filters-table">
          {filtersLoading ? (
            <p className="text-muted mb-0">Loading filters…</p>
          ) : senderFilters.length === 0 ? (
            <p className="text-muted mb-0">No filters yet.</p>
          ) : (
            <table className="table mt-3">
              <thead>
                <tr>
                  <th>Sender</th>
                  <th>Scope</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {senderFilters.map((filter) => (
                  <tr key={filter.id}>
                    <td>
                      <strong>{filter.sender_pattern}</strong>
                    </td>
                    <td>
                      {filter.scope === "global" ? (
                        <span className="badge bg-primary-subtle text-primary-emphasis">Global</span>
                      ) : (
                        <div>
                          <span className="badge bg-secondary-subtle text-secondary-emphasis">Company</span>
                          <div className="mono small mt-1">{filter.company_id}</div>
                        </div>
                      )}
                    </td>
                    <td>{filter.note || <span className="text-muted">—</span>}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => confirmDeleteFilter(filter)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      </div>
      <ConfirmDialog {...confirmProps} />
    </>
  );
}
